// SYSTEMS BY LEVEL AND AREA, on construction tracking (owner, 2026-10-26:
// "add a system panel on the construction tracking, that shows all
// available systems, per area, per level, each in its own tab, based on all
// levels in levels/areas and system, and make sure there is an option to
// add missing systems so they can be synced to levels, areas, systems and
// then can be added to the schedule").
//
// One tab per level page of Levels, Areas & Systems, in its order. Under
// each, every area of that level: the systems the area has (with how many
// tagged model elements of each sit on THIS level), the systems its tagged
// elements here point to that the area does not have yet (Add), the systems
// other areas of the level have (quick add), and a picker for any other
// name. Every add is the same write the Levels, Areas & Systems page makes
// (PUT /areas/:id/systems, or accept for a suggested one), so the two pages
// never disagree. "Add to schedule" opens the schedule builder that lays
// out level > area > system tasks from exactly these systems.
//
// The per-level counts read every tagged element (28 s on BA03 the first
// time, remembered after), so the panel opens on the systems at once and
// fills the counts in when they arrive.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Drawer, Empty, Select, Space, Spin, Table, Tabs, Tag, Tooltip, message } from "antd";
import { PlusOutlined, ReloadOutlined, ScheduleOutlined } from "@ant-design/icons";
import {
  listLevels, listAreaSystems, listAreaSystemsByLevel, setAreaSystems, acceptAreaSystems,
} from "api/areas/areasApi";
import SystemTasksBuilder from "components/Coordination/SystemTasksBuilder";
import { levelTabs, addPlan } from "./systemsPanelModel";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

export default function SystemsPanel({ projectId, open, onClose }) {
  const [pages, setPages] = useState(null);
  const [areas, setAreas] = useState([]);
  const [vocabulary, setVocabulary] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [placement, setPlacement] = useState(null);
  const [byLevel, setByLevel] = useState(null);
  const [counting, setCounting] = useState(false);
  const [countError, setCountError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("");
  const [buildOpen, setBuildOpen] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true); setError(""); setCountError("");
    try {
      const [lv, sys] = await Promise.all([listLevels(projectId), listAreaSystems(projectId)]);
      setPages(lv.pages || []);
      setAreas(sys.areas || []);
      setVocabulary(sys.vocabulary || []);
      setCanEdit(!!sys.canEdit);
      setPlacement(sys.placement || null);
      if (sys.refreshing) {
        listAreaSystems(projectId, { fresh: true }).then((d) => setAreas(d.areas || [])).catch(() => {});
      }
    } catch (e) {
      setError(e.message || "Could not read the levels and systems");
    } finally {
      setLoading(false);
    }
    setCounting(true);
    try {
      setByLevel(await listAreaSystemsByLevel(projectId));
    } catch (e) {
      setCountError(e.message || "Could not count the tagged elements per level");
    } finally {
      setCounting(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const tabs = useMemo(() => levelTabs({ pages, areas, byLevel }), [pages, areas, byLevel]);
  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.pageId === tab)) setTab(tabs[0].pageId);
  }, [tabs, tab]);

  // The route's answer for one area replaces that area's row.
  const take = (reply) => {
    if (!reply || !reply.areaId) return;
    setAreas((list) => list.map((a) => (String(a.areaId) === String(reply.areaId)
      ? { ...a, systems: reply.systems, suggestions: reply.suggestions, unmapped: reply.unmapped } : a)));
  };

  const addTo = async (areaId, names) => {
    const area = areas.find((a) => String(a.areaId) === String(areaId));
    const plan = addPlan(area, names);
    if (!plan.accept.length && !plan.add.length) return 0;
    if (plan.accept.length) {
      try {
        take(await acceptAreaSystems(projectId, areaId, { names: plan.accept }));
      } catch (e) {
        // The suggestions moved since this panel read them: add by hand.
        plan.add.push(...plan.accept);
      }
    }
    if (plan.add.length) take(await setAreaSystems(projectId, areaId, { add: plan.add }));
    return plan.accept.length + plan.add.length;
  };

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { message.error(e.message || "Could not add the system"); } finally { setBusy(""); }
  };

  const addOne = (row, names) => run(`${row.areaId}`, async () => {
    const n = await addTo(row.areaId, names);
    if (n) message.success(`${names.join(", ")} added to ${row.name} - on Levels, Areas & Systems too`);
  });

  const addAllMissing = (t) => run(`level:${t.pageId}`, async () => {
    let n = 0; let areasDone = 0;
    for (const r of t.rows) {
      if (!r.missing.length) continue;
      // eslint-disable-next-line no-await-in-loop
      n += await addTo(r.areaId, r.missing.map((m) => m.name));
      areasDone += 1;
    }
    message.success(`${n} system${n === 1 ? "" : "s"} added across ${areasDone} area${areasDone === 1 ? "" : "s"} of ${t.name}`);
  });

  const columns = (t) => [
    {
      title: "Area",
      width: 150,
      render: (r) => (
        <span>
          {r.color && <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: r.color, marginRight: 6 }} />}
          <b>{r.name}</b>
          {r.status !== "approved" && <Tag color="orange" style={{ marginLeft: 6 }}>{r.status}</Tag>}
          {r.total != null && <div style={{ fontSize: 11, color: "#8D8D8D" }}>{fmt(r.total)} tagged on {t.name}</div>}
        </span>
      ),
    },
    {
      title: "Systems of the area",
      render: (r) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }} data-testid={`systems-on-${r.areaId}`}>
          {r.onArea.length === 0 && <span style={{ fontSize: 12, color: "#b45309" }}>None yet - it cannot go on the schedule</span>}
          {r.onArea.map((s) => (
            <Tooltip key={s.name} title={s.count == null ? "Counting..." : `${fmt(s.count)} tagged element${s.count === 1 ? "" : "s"} of this area on ${t.name}`}>
              <Tag color={s.count === 0 ? "default" : "blue"} style={{ marginRight: 0 }}>
                {s.name}{s.count != null ? ` · ${fmt(s.count)}` : ""}
              </Tag>
            </Tooltip>
          ))}
        </div>
      ),
    },
    {
      title: "Missing",
      render: (r) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }} data-testid={`missing-${r.areaId}`}>
          {r.missing.map((m) => (
            <Tooltip key={m.name} title={m.areaWide
              ? `${fmt(m.count)} tagged elements of this area (all levels) are ${m.name}`
              : `${fmt(m.count)} tagged elements of this area on ${t.name} are ${m.name}, and the area does not list it`}
            >
              <Button
                size="small" type="dashed" icon={<PlusOutlined />}
                disabled={!canEdit || !!busy}
                onClick={() => addOne(r, [m.name])}
                style={{ borderColor: "#d97706", color: "#b45309" }}
              >
                {m.name} {fmt(m.count)}
              </Button>
            </Tooltip>
          ))}
          {r.elsewhere.length > 0 && (
            <span style={{ fontSize: 11, color: "#8D8D8D" }}>
              {r.missing.length > 0 ? " · " : ""}On other areas here:{" "}
              {r.elsewhere.map((n, i) => (
                <span key={n}>
                  {i > 0 && ", "}
                  {canEdit
                    ? (
                      <Tooltip title={`No tagged element of this area on ${t.name} is ${n}. Add it if the area has it anyway (for example when its elements were tagged to a smaller zone inside it).`}>
                        <Button type="link" size="small" style={{ padding: 0, height: "auto", fontSize: 11 }} disabled={!!busy} onClick={() => addOne(r, [n])}>+ {n}</Button>
                      </Tooltip>
                    )
                    : n}
                </span>
              ))}
            </span>
          )}
          {!r.missing.length && !r.elsewhere.length && <span style={{ fontSize: 12, color: "#8D8D8D" }}>-</span>}
        </div>
      ),
    },
    {
      title: "Add a system",
      width: 200,
      render: (r) => (canEdit ? (
        <Select
          mode="tags" size="small" style={{ width: "100%" }} value={[]}
          disabled={!!busy}
          placeholder={<span><PlusOutlined /> Pick or type</span>}
          onSelect={(v) => addOne(r, [v])}
          options={(vocabulary || [])
            .filter((o) => !r.onArea.some((s) => s.name.toLowerCase() === String(o.value).toLowerCase()))
            .map((o) => ({ value: o.value, label: o.label }))}
        />
      ) : null),
    },
  ];

  const items = tabs.map((t) => ({
    key: t.pageId,
    label: (
      <span data-testid={`systems-tab-${t.pageId}`}>
        {t.name}{" "}
        {t.missingCount > 0 && <Badge count={t.missingCount} size="small" style={{ background: "#d97706" }} title={`${t.missingCount} missing`} />}
      </span>
    ),
    children: (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#5A6B73" }}>
            {t.counted
              ? (t.systems.length ? <>On {t.name}: {t.systems.map((s) => `${s.name} ${fmt(s.count)}`).join(" · ")}</> : <>No tagged elements on {t.name}.</>)
              : (counting ? <><Spin size="small" /> Counting the tagged elements on each level...</> : null)}
          </span>
          <span style={{ flex: 1 }} />
          {canEdit && t.missingCount > 0 && (
            <Button size="small" type="primary" ghost icon={<PlusOutlined />} loading={busy === `level:${t.pageId}`} disabled={!!busy} onClick={() => addAllMissing(t)}>
              Add all {t.missingCount} missing on {t.name}
            </Button>
          )}
        </div>
        {t.rows.length === 0
          ? <Empty description={`${t.name} has no areas yet`} />
          : <Table size="small" rowKey="areaId" pagination={false} dataSource={t.rows} columns={columns(t)} />}
      </div>
    ),
  }));

  return (
    <Drawer
      title="Systems by level and area"
      width={Math.min(1100, typeof window !== "undefined" ? window.innerWidth - 40 : 1100)}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={(
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading || counting}>Refresh</Button>
          <Button size="small" onClick={() => window.open(`/areas/${projectId}`, "_blank")}>Levels, Areas &amp; Systems</Button>
          <Tooltip title="Lay out level > area > system tasks on the coordination and field install schedules from these systems (preview first, nothing is written before Build)">
            <Button size="small" type="primary" icon={<ScheduleOutlined />} onClick={() => setBuildOpen(true)} disabled={!projectId}>
              Add to schedule
            </Button>
          </Tooltip>
        </Space>
      )}
    >
      <div style={{ fontSize: 12, color: "#5A6B73", marginBottom: 10 }}>
        The systems of each area, level by level, with the model elements tagged to the area on that level.
        A system added here is the area's system on every level that shows the area, and on Levels, Areas &amp; Systems.
        {placement && placement.why ? <div style={{ marginTop: 4, color: "#8D8D8D" }}>{placement.why}</div> : null}
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
      {countError && <Alert type="warning" showIcon message={`Per-level counts unavailable: ${countError}. Showing the area-wide suggestions.`} style={{ marginBottom: 8 }} />}
      {!canEdit && !loading && pages && (
        <Alert type="info" showIcon message="You can see the systems. Adding them needs the Levels & Areas edit permission." style={{ marginBottom: 8 }} />
      )}
      {loading && !pages ? <Spin /> : null}
      {pages && !tabs.length && !loading && (
        <Empty description={<>No levels yet. Add them on <a href={`/areas/${projectId}`} target="_blank" rel="noopener noreferrer">Levels, Areas &amp; Systems</a>.</>} />
      )}
      {tabs.length > 0 && <Tabs activeKey={tab} onChange={setTab} items={items} />}
      <SystemTasksBuilder projectId={projectId} open={buildOpen} onClose={() => setBuildOpen(false)} />
    </Drawer>
  );
}
